'use client';
import { useState, FormEvent } from 'react';
import Link from 'next/link';
import { useT } from '@/lib/i18n';

export default function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail]   = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [error, setError]   = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error((d as { error?: string }).error ?? 'Error');
      }
      setStatus('sent');
    } catch (err: unknown) {
      setError((err as Error).message);
      setStatus('error');
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold mb-2 text-center">{t('auth.forgotPassword')}</h1>
        <p className="text-sm text-slate-400 text-center mb-8">{t('auth.forgotPasswordHint')}</p>

        {status === 'sent' ? (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4 text-sm text-center">
            {t('auth.forgotPasswordSent')}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-slate-500">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="bg-sky-100 border border-sky-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:border-violet-500"
              />
            </div>
            {status === 'error' && <p className="text-red-400 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-lg px-4 py-2 font-medium transition-colors"
            >
              {status === 'loading' ? t('auth.sending') : t('auth.sendResetLink')}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-slate-500">
          <Link href="/login" className="text-violet-400 hover:text-violet-300">
            ← {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
